import {
  IBaseMeasures,
  ICanBeChartedProperty,
  ICanQueryByTimestampProperty,
  ReportingProperty,
  isBaseMeasures,
  isCanBeChartedProperty,
  isCanGroupMeasuresProperty,
  isCanQueryByTimestampProperty,
} from '@principle-theorem/reporting/interfaces';
import { getKeyValues, isArray, isObject } from '@principle-theorem/shared';
import { intersection, keys, memoize } from 'lodash';
import { BaseMeasures } from './base-measures';
import {
  CanBeChartedProperty,
  CanDoAllProperty,
  CanGroupMeasuresProperty,
} from './measure-properties';

export class KeyOverlapError extends Error {
  constructor(factTableId: string, overlappingKeys: string[]) {
    const keyList = overlappingKeys.join(', ');
    super(`Fact table (${factTableId}) has duplicate keys: ${keyList}`);
  }
}

export function getFactTablePropertyMap(
  factTable: IBaseMeasures
): Record<string, ReportingProperty> {
  return getKeyValues(factTable)
    .map(([_key, value]) => value)
    .reduce((acc: Record<string, ReportingProperty>, property) => {
      const newProperties = getPropertyMap(property);
      const overlappingKeys = intersection(keys(acc), keys(newProperties));
      if (overlappingKeys.length > 0) {
        // eslint-disable-next-line no-console
        console.error('overlappingKeys', acc, newProperties, overlappingKeys);
      }
      return { ...acc, ...newProperties };
    }, {});
}

function getPropertyMap(property: unknown): Record<string, ReportingProperty> {
  if (isBaseMeasures(property)) {
    return getBaseMeasuresPropertyMap(property);
  }
  return {};
}

function getBaseMeasuresPropertyMap(
  baseMeasures: IBaseMeasures
): Record<string, ReportingProperty> {
  return collectResolvableProperties(baseMeasures, baseMeasures.id);
}

/**
 * Walk a measure container and collect every resolvable leaf property,
 * keyed by its metadata id. Recurses into nested measure-group objects
 * (e.g. `PatientRecallMeasures`, `PatientCombinedRecallMeasures`) so their
 * leaves surface at the fact-table level.
 *
 * Recursion boundary:
 * - `isResolvableProperty` → leaf, register by id
 * - `isMeasureGroup` → nested group (class instance that isn't a leaf and
 *   isn't a `BaseMeasures`), recurse
 * - Everything else (primitives, plain objects, arrays, other `BaseMeasures`)
 *   → skip. `BaseMeasures` children are handled at the fact-table level by
 *   `getFactTablePropertyMap` to avoid duplicate walks.
 */
function collectResolvableProperties(
  container: object,
  factId: string
): Record<string, ReportingProperty> {
  return getKeyValues(container).reduce(
    (acc: Record<string, ReportingProperty>, [_key, value]) => {
      if (isResolvableProperty(value)) {
        if (acc[value.metadata.id]) {
          throw new KeyOverlapError(factId, [value.metadata.id]);
        }
        return { ...acc, [value.metadata.id]: value };
      }
      if (isMeasureGroup(value)) {
        const nested = collectResolvableProperties(value, factId);
        const overlappingKeys = intersection(keys(acc), keys(nested));
        if (overlappingKeys.length > 0) {
          throw new KeyOverlapError(factId, overlappingKeys);
        }
        return { ...acc, ...nested };
      }
      return acc;
    },
    {}
  );
}

function isResolvableProperty(
  property: unknown
): property is CanBeChartedProperty | CanGroupMeasuresProperty {
  return (
    property instanceof CanBeChartedProperty ||
    property instanceof CanGroupMeasuresProperty
  );
}

/**
 * A "measure group" is a non-leaf, non-BaseMeasures class instance that
 * namespaces resolvable properties (e.g. `patient.recall`, `patient.combinedRecall`).
 * We exclude plain objects and arrays to avoid descending into query state
 * (`{attributes: [], filters: [], ...}`), inherited data, etc.
 */
function isMeasureGroup(value: unknown): value is object {
  if (!isObject(value)) {
    return false;
  }
  if (isArray(value)) {
    return false;
  }
  if (Object.getPrototypeOf(value) === Object.prototype) {
    return false;
  }
  if (isResolvableProperty(value)) {
    return false;
  }
  if (isBaseMeasures(value)) {
    return false;
  }
  return true;
}

export function resolveReportingProperty(
  factTable: IBaseMeasures,
  id: string
): ReportingProperty | undefined {
  const getMapFn = memoize(
    getFactTablePropertyMap,
    (factMeasure) => factMeasure.id
  );
  const propertyMap = getMapFn(factTable);
  const result = propertyMap[id];
  if (!result) {
    // eslint-disable-next-line no-console
    console.warn(`Can't resolve property ${id} on ${factTable.id}`);
    return;
  }
  return result;
}

export function resolveCanGroupMeasuresProperty(
  factTable: BaseMeasures,
  id: string
): CanGroupMeasuresProperty | undefined {
  const property = resolveReportingProperty(factTable, id);
  if (!isCanGroupMeasuresProperty(property)) {
    return;
  }
  return property;
}

export function resolveCanBeChartedProperty(
  factTable: IBaseMeasures,
  id: string
): ICanBeChartedProperty | undefined {
  const property = resolveReportingProperty(factTable, id);
  if (!isCanBeChartedProperty(property)) {
    return;
  }
  return property;
}

export function resolveCanQueryByTimestampProperty(
  factTable: BaseMeasures,
  targetTimestamp: string
): (CanDoAllProperty & ICanQueryByTimestampProperty) | undefined {
  const timestampProperty = resolveReportingProperty(
    factTable,
    targetTimestamp
  );
  if (!isCanQueryByTimestampProperty(timestampProperty)) {
    return;
  }
  return timestampProperty as CanDoAllProperty & ICanQueryByTimestampProperty;
}
